import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import DeleteShipmentModal from "@/components/DeleteShipmentModal";
import api, { formatApiError, REQUIRED_DOC_TYPES, DOC_TYPE_LABELS } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { MagnifyingGlass, Plus, X, FunnelSimple, CheckCircle, WarningCircle, Package, TrashSimple, FolderOpen, DownloadSimple, FloppyDisk } from "@phosphor-icons/react";
import { isFolderSupported, getSavedIds } from "@/lib/localFolder";

const EMPTY = {
  q: "", awb_number: "", customs_number: "", drive: "",
  status: "", date_from: "", date_to: "", backup: "",
};

function toCsv(items) {
  const headers = [
    "AWB", "Date", "Status", "Missing", "Drive", "Consignee",
    "Customs 1", "Customs 2", "AWB Form", "Invoice", "Permit", "Terminal Charges", "Customs Form", "Notes",
  ];
  const rows = items.map((s) => [
    s.awb_number, s.doc_date, s.is_complete ? "Complete" : "Incomplete",
    s.docs_missing.map((t) => t).join("|"), s.drive, s.consignee || "",
    s.customs_no_1 || "", s.customs_no_2 || "",
    s.docs_present.includes("awb_form") ? "yes" : "no",
    s.docs_present.includes("invoice") ? "yes" : "no",
    s.docs_present.includes("permit") ? "yes" : "no",
    s.docs_present.includes("terminal_charges") ? "yes" : "no",
    s.docs_present.includes("customs_form") ? "yes" : "no",
    (s.notes || "").replace(/\s+/g, " "),
  ]);
  const esc = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [headers.map(esc).join(","), ...rows.map((r) => r.map(esc).join(","))].join("\n");
}

export default function Shipments() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(EMPTY);
  const [drives, setDrives] = useState([]);
  const [toDelete, setToDelete] = useState(null);
  const [savedMap, setSavedMap] = useState({});
  const [sortKey, setSortKey] = useState(null); // "status" | "awb_number" | "doc_date" | "customs"
  const [sortDir, setSortDir] = useState("asc"); // "asc" | "desc"
  const backupSupported = isFolderSupported();

  const toggleSort = (key) => {
    if (sortKey !== key) { setSortKey(key); setSortDir("asc"); }
    else if (sortDir === "asc") setSortDir("desc");
    else { setSortKey(null); setSortDir("asc"); }
  };

  const load = useCallback(async (f = filters) => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(f).forEach(([k, v]) => { if (v && k !== "backup") params[k] = v; });
      const { data } = await api.get("/shipments", { params });
      setItems(data);
      // Load saved-locally markers for all documents across all shipments
      const allDocIds = data.flatMap((s) => s.document_ids || []);
      if (allDocIds.length > 0) {
        const m = await getSavedIds(allDocIds);
        setSavedMap(m);
      } else {
        setSavedMap({});
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [filters]);

  // Compute backup status per shipment
  const backupStatus = (s) => {
    const ids = s.document_ids || [];
    if (ids.length === 0) return "empty";
    const savedCount = ids.filter((id) => savedMap[id]).length;
    if (savedCount === 0) return "none";
    if (savedCount === ids.length) return "full";
    return "partial";
  };

  // Client-side backup filter + sort
  const displayed = (() => {
    const filtered = items.filter((s) => {
      if (!filters.backup) return true;
      const st = backupStatus(s);
      if (filters.backup === "unsaved") return st === "none" || st === "partial";
      if (filters.backup === "partial") return st === "partial";
      if (filters.backup === "full") return st === "full";
      if (filters.backup === "none") return st === "none";
      return true;
    });
    if (!sortKey) return filtered;
    const dir = sortDir === "asc" ? 1 : -1;
    const getVal = (s) => {
      switch (sortKey) {
        case "status":     return s.is_complete ? 1 : 0;
        case "awb_number": return (s.awb_number || "").toLowerCase();
        case "doc_date":   return s.doc_date || "";
        case "customs":    return (s.customs_no_1 || s.customs_no_2 || "").toLowerCase();
        default: return "";
      }
    };
    return [...filtered].sort((a, b) => {
      const va = getVal(a), vb = getVal(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return  1 * dir;
      return 0;
    });
  })();

  useEffect(() => {
    load(EMPTY);
    api.get("/drives").then(({ data }) => setDrives(data)).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const apply = (e) => { e?.preventDefault(); load(filters); };
  const clear = () => { setFilters(EMPTY); load(EMPTY); };
  const upd = (k) => (e) => setFilters({ ...filters, [k]: e.target.value });

  const del = (s) => setToDelete(s);

  return (
    <Layout>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono mb-2">Filing Monitor</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-zinc-950">Shipments</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-xl">
            Every shipment needs 5 required documents. Missing ones are highlighted in <span className="font-mono text-[#FF4F00]">orange</span>.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => {
            if (displayed.length === 0) { toast.error("Nothing to export"); return; }
            const csv = toCsv(displayed);
            const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `cargodocs-shipments-${new Date().toISOString().slice(0, 10)}.csv`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success(`Exported ${displayed.length} shipments`);
          }} data-testid="export-csv-btn"
            className="bg-white text-zinc-900 border border-zinc-200 text-sm px-4 py-2.5 flex items-center gap-2 hover:bg-zinc-100 transition-colors duration-150">
            <DownloadSimple size={16} /> Export CSV
          </button>
          <Link to="/shipments/new" data-testid="new-shipment-btn"
            className="bg-[#002FA7] text-white text-sm px-4 py-2.5 flex items-center gap-2 hover:bg-[#00227A] transition-colors duration-150">
            <Plus size={16} /> New Shipment
          </Link>
        </div>
      </div>

      {/* Filters */}
      <form onSubmit={apply} className="bg-white border border-zinc-200 p-4 mb-4" data-testid="filters-form">
        <div className="flex items-center gap-2 mb-3">
          <FunnelSimple size={16} className="text-zinc-500" />
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Filter shipments</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <F placeholder="Global search" value={filters.q} onChange={upd("q")} testid="filter-q" icon={<MagnifyingGlass size={14} />} />
          <F placeholder="AWB number" value={filters.awb_number} onChange={upd("awb_number")} testid="filter-awb" mono />
          <F placeholder="Customs no." value={filters.customs_number} onChange={upd("customs_number")} testid="filter-customs" mono />
          <FS value={filters.status} onChange={upd("status")} testid="filter-status">
            <option value="">All statuses</option>
            <option value="incomplete">Incomplete only</option>
            <option value="complete">Complete only</option>
          </FS>
          <FS value={filters.drive} onChange={upd("drive")} testid="filter-drive">
            <option value="">All drives</option>
            {drives.map((d) => <option key={d.letter} value={d.letter}>Drive {d.letter}:</option>)}
          </FS>
          <F type="date" value={filters.date_from} onChange={upd("date_from")} testid="filter-date-from" mono />
          <F type="date" value={filters.date_to} onChange={upd("date_to")} testid="filter-date-to" mono />
          {backupSupported && (
            <FS value={filters.backup} onChange={upd("backup")} testid="filter-backup">
              <option value="">All backups</option>
              <option value="unsaved">Unsaved locally</option>
              <option value="none">Not saved</option>
              <option value="partial">Partially saved</option>
              <option value="full">Fully saved</option>
            </FS>
          )}
        </div>
        <div className="flex items-center gap-2 mt-3">
          <button type="submit" data-testid="apply-filters-btn"
            className="bg-[#002FA7] text-white text-sm px-4 py-2 hover:bg-[#00227A] transition-colors duration-150 flex items-center gap-2">
            <MagnifyingGlass size={14} /> Search
          </button>
          <button type="button" onClick={clear} data-testid="clear-filters-btn"
            className="border border-zinc-200 bg-white text-sm px-3 py-2 hover:bg-zinc-100 transition-colors duration-150 flex items-center gap-2">
            <X size={14} /> Clear
          </button>
          <div className="ml-auto text-xs text-zinc-500 font-mono" data-testid="results-count">
            {loading ? "loading…" : `${displayed.length}${displayed.length !== items.length ? ` of ${items.length}` : ""} shipment${displayed.length === 1 ? "" : "s"}`}
          </div>
        </div>
      </form>

      {/* Table */}
      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="border-b border-zinc-200 text-[10px] uppercase font-mono text-zinc-500 bg-zinc-50">
              <SortableTh label="Status"     sortKey="status"     activeKey={sortKey} dir={sortDir} onClick={() => toggleSort("status")} testid="sort-status" />
              <SortableTh label="AWB Number" sortKey="awb_number" activeKey={sortKey} dir={sortDir} onClick={() => toggleSort("awb_number")} testid="sort-awb" />
              <SortableTh label="Date"       sortKey="doc_date"   activeKey={sortKey} dir={sortDir} onClick={() => toggleSort("doc_date")} testid="sort-date" />
              <SortableTh label="Customs"    sortKey="customs"    activeKey={sortKey} dir={sortDir} onClick={() => toggleSort("customs")} testid="sort-customs" />
              {REQUIRED_DOC_TYPES.map((t) => (
                <th key={t} className="text-center px-2 py-3">{shortLabel(t)}</th>
              ))}
              <th className="text-left px-4 py-3">Drive</th>
              {backupSupported && <th className="text-center px-2 py-3">Backup</th>}
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={backupSupported ? 12 : 11} className="text-center py-16 text-zinc-500 font-mono text-sm">Loading…</td></tr>
            ) : displayed.length === 0 ? (
              <tr><td colSpan={backupSupported ? 12 : 11} className="text-center py-16 text-zinc-500">
                <Package size={40} weight="duotone" className="mx-auto text-zinc-300 mb-3" />
                <div className="font-mono text-sm">
                  {filters.backup ? `No shipments match "${filters.backup}" backup status.` : "No shipments match your filters."}
                </div>
                <Link to="/shipments/new" className="text-[#002FA7] hover:underline text-sm mt-2 inline-block">
                  Create your first shipment →
                </Link>
              </td></tr>
            ) : displayed.map((s) => (
              <tr key={s.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors duration-150 cursor-pointer"
                  onClick={() => navigate(`/shipments/${s.id}`)}
                  data-testid={`ship-row-${s.id}`}>
                <td className="px-4 py-3">
                  {s.is_complete ? (
                    <span className="inline-flex items-center gap-1 text-[11px] font-mono border border-emerald-200 bg-emerald-50 text-emerald-800 px-2 py-0.5">
                      <CheckCircle size={12} weight="fill" /> COMPLETE
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[11px] font-mono border border-orange-200 bg-orange-50 text-[#FF4F00] px-2 py-0.5">
                      <WarningCircle size={12} weight="fill" /> {s.docs_missing.length} MISSING
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 font-mono text-zinc-900 font-medium">{s.awb_number}</td>
                <td className="px-4 py-3 font-mono text-zinc-700">{s.doc_date || "—"}</td>
                <td className="px-4 py-3 font-mono text-zinc-700 text-xs">
                  {s.customs_no_1 || "—"}{s.customs_no_2 ? <><br />{s.customs_no_2}</> : ""}
                </td>
                {REQUIRED_DOC_TYPES.map((t) => {
                  const has = s.docs_present.includes(t);
                  return (
                    <td key={t} className="px-2 py-3 text-center">
                      {has ? (
                        <CheckCircle size={18} weight="fill" className="inline text-emerald-600" data-testid={`ship-${s.id}-${t}-ok`} />
                      ) : (
                        <span className="inline-block w-4 h-4 border-2 border-orange-300 bg-orange-50 rounded-sm" title="Missing" data-testid={`ship-${s.id}-${t}-missing`} />
                      )}
                    </td>
                  );
                })}
                <td className="px-4 py-3 font-mono text-zinc-700">{s.drive}:</td>
                {backupSupported && (
                  <td className="px-2 py-3 text-center" data-testid={`backup-${s.id}`}>
                    {(() => {
                      const st = backupStatus(s);
                      if (st === "empty") return <span className="text-[9px] font-mono text-zinc-300" title="No documents">—</span>;
                      if (st === "full") return <FloppyDisk size={16} weight="fill" className="inline text-emerald-600" data-testid={`backup-full-${s.id}`} title="All docs saved locally" />;
                      if (st === "partial") return <FloppyDisk size={16} weight="regular" className="inline text-amber-500" data-testid={`backup-partial-${s.id}`} title="Some docs saved locally" />;
                      return <FloppyDisk size={16} weight="regular" className="inline text-zinc-300" data-testid={`backup-none-${s.id}`} title="Not saved locally" />;
                    })()}
                  </td>
                )}
                <td className="px-4 py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  <Link to={`/shipments/${s.id}`} data-testid={`ship-open-${s.id}`}
                    className="inline-flex items-center gap-1 text-xs text-[#002FA7] px-2 py-1 hover:bg-blue-50 transition-colors duration-150">
                    <FolderOpen size={14} /> Open
                  </Link>
                  {(user?.role === "admin" || s.created_by_id === user?.id) && (
                    <button onClick={() => del(s)} data-testid={`ship-del-${s.id}`}
                      className="inline-flex items-center gap-1 text-xs text-red-700 px-2 py-1 hover:bg-red-50 transition-colors duration-150 ml-1">
                      <TrashSimple size={14} /> Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {toDelete && (
        <DeleteShipmentModal
          shipment={toDelete}
          onClose={() => setToDelete(null)}
          onDeleted={() => setItems((xs) => xs.filter((s) => s.id !== toDelete.id))}
        />
      )}
    </Layout>
  );
}

function shortLabel(t) {
  return {
    awb_form: "AWB",
    invoice: "INV",
    permit: "PERMIT",
    terminal_charges: "TERM",
    customs_form: "CUSTOMS",
  }[t] || DOC_TYPE_LABELS[t];
}

function SortableTh({ label, sortKey, activeKey, dir, onClick, testid }) {
  const active = activeKey === sortKey;
  const arrow = !active ? "↕" : dir === "asc" ? "↑" : "↓";
  return (
    <th className="text-left px-4 py-3 select-none">
      <button
        type="button"
        onClick={onClick}
        data-testid={testid}
        className={`inline-flex items-center gap-1 uppercase tracking-[0.05em] hover:text-zinc-900 transition-colors duration-150 ${active ? "text-zinc-900" : "text-zinc-500"}`}
        title={active ? (dir === "asc" ? "Sorted A→Z (click for Z→A)" : "Sorted Z→A (click to clear)") : "Click to sort A→Z"}
      >
        {label}
        <span className={`font-mono text-[11px] ${active ? "text-[#002FA7]" : "text-zinc-400"}`}>{arrow}</span>
      </button>
    </th>
  );
}

function F({ icon, mono, ...props }) {
  return (
    <div className="relative">
      {icon && <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400">{icon}</span>}
      <input
        {...props}
        className={`w-full border border-zinc-200 px-3 py-2 text-sm ${icon ? "pl-8" : ""} ${mono ? "font-mono" : ""} focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent`}
        data-testid={props.testid}
      />
    </div>
  );
}
function FS({ children, ...props }) {
  return (
    <select {...props}
      className="w-full border border-zinc-200 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
      data-testid={props.testid}>
      {children}
    </select>
  );
}
